import {
  ComponentProps,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { offsetLeft, offsetTop } from '@/utils/layout';
import ContextMenu from "../ContextMenu";

interface UseContextMenuProps {
  onHide?: () => void
}

const useContextMenu = (
  {
    onHide
  }: UseContextMenuProps = {}) => {
  const [visible, setVisible] = useState(false)
  const [contextMenuProps, setContextMenuProps] = useState<Partial<ComponentProps<typeof ContextMenu>>>({})
  const onHideRef = useRef<() => void>()
  onHideRef.current = onHide
  const clickEvent = useRef<MouseEvent | TouchEvent>();

  const show = useCallback((e: MouseEvent | TouchEvent | SyntheticEvent<Element, TouchEvent | MouseEvent>) => {
    const event: MouseEvent | TouchEvent = 'nativeEvent' in e ? e.nativeEvent : e
    clickEvent.current = event
    let offsetY = 0
    let offsetX = 0
    const pageY = event instanceof MouseEvent
      ? event.pageY
      : event instanceof TouchEvent ? event.changedTouches[0].pageY : 0
    const pageX = event instanceof MouseEvent
      ? event.pageX
      : event instanceof TouchEvent ? event.changedTouches[0].pageX : 0
    const target = e.currentTarget || e.target
    if (target instanceof Element) {
      offsetY = pageY - offsetTop(target) - document.documentElement.scrollTop
      offsetX = pageX - offsetLeft(target) - document.documentElement.scrollLeft
    }

    setContextMenuProps({
      element: target instanceof Element ? target : null,
      elementOffset: {
        x: offsetX,
        y: offsetY
      }
    })
    setVisible(true)
  }, [])

  const hide = useCallback((e?: MouseEvent | TouchEvent) => {
    if (e && e === clickEvent.current) {
      return
    }
    setVisible(false)
    onHideRef.current?.()
  }, [])

  useEffect(() => {
    if (visible) {
      window.addEventListener('click', hide)
    }

    return () => {
      window.removeEventListener('click', hide)
    }
  }, [visible, hide])

  return {
    visible,
    show,
    hide,
    contextMenuProps
  }
}

export default useContextMenu
